import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { format, parse } from "date-fns";

interface RevenueTrendChartProps {
  data: Array<{ month: string; totalInCents: number }>;
}

function formatMonth(month: string) {
  // month is "YYYY-MM"
  try {
    return format(parse(month, "yyyy-MM", new Date()), "MMM yyyy");
  } catch {
    return month;
  }
}

function formatCurrency(amount: number) {
  return `$${amount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function RevenueTrendChart({ data }: RevenueTrendChartProps) {
  const chartData = data.map((d) => ({
    month: formatMonth(d.month),
    revenue: d.totalInCents,
    amount: d.totalInCents,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg p-3">
          <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            {label}
          </p>
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {formatCurrency(payload[0].payload.amount)}
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart
        data={chartData}
        margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
      >
        <defs>
          <linearGradient id="revenue-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#001544" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#001544" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="#e2e8f0"
          className="dark:stroke-slate-800"
        />
        <XAxis
          dataKey="month"
          tick={{ fill: "#64748b", fontSize: 12 }}
          axisLine={{ stroke: "#e2e8f0" }}
        />
        <YAxis
          tickFormatter={(v) => `$${v.toLocaleString()}`}
          tick={{ fill: "#64748b", fontSize: 12 }}
          axisLine={{ stroke: "#e2e8f0" }}
          width={70}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="revenue"
          stroke="#001544"
          strokeWidth={2.5}
          fill="url(#revenue-gradient)"
          name="Revenue"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
